"use client";

import { useMemo, useState } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import {
  Building2,
  CarFront,
  CheckCircle2,
  ChevronDown,
  FileUp,
  Globe,
  Image as ImageIcon,
  Loader2,
  MapPin,
  ShieldCheck,
  Smartphone,
  Timer,
} from "lucide-react";
import CountrySelectModal, { COUNTRIES_STATIC } from "@/components/CountrySelectModal";
import { SocialProviderButton, type SocialProvider } from "@/components/auth/SocialProviderButton";
import { showroomApi } from "@/lib/api/showroom";
import { Button } from "@/components/ui/button";
import { authApi } from "@/lib/api/auth";

type RegisterStatus = "draft" | "pending" | "approved" | "rejected";

const statusMeta: Record<RegisterStatus, { label: string; color: string }> = {
  draft: { label: "مسودة", color: "bg-slate-100 text-slate-700" },
  pending: { label: "قيد المراجعة", color: "bg-amber-100 text-amber-700" },
  approved: { label: "معتمد", color: "bg-emerald-100 text-emerald-700" },
  rejected: { label: "مرفوض", color: "bg-red-100 text-red-700" },
};

function InputField({
  label,
  value,
  onChange,
  placeholder,
  type = "text",
  required = false,
  dir = "rtl",
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  type?: string;
  required?: boolean;
  dir?: "rtl" | "ltr";
}) {
  return (
    <div>
      <label className="block text-sm font-bold text-blue-100 mb-1.5">
        {label} {required ? "*" : ""}
      </label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        dir={dir}
        className="w-full bg-white/10 border border-white/20 text-white placeholder-white/40 rounded-2xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
        placeholder={placeholder}
      />
    </div>
  );
}

export default function ShowroomRegisterPage() {
  const router = useRouter();
  const [step, setStep] = useState<1 | 2>(1);
  const [loading, setLoading] = useState(false);
  const [socialLoading, setSocialLoading] = useState<SocialProvider | null>(null);
  const [otpLoading, setOtpLoading] = useState(false);
  const [error, setError] = useState("");
  const [countryModalOpen, setCountryModalOpen] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState(
    COUNTRIES_STATIC.find((c) => c.code === "OM") || COUNTRIES_STATIC[0],
  );

  const [form, setForm] = useState({
    company_name: "",
    mobile_local: "",
    otp_code: "",
    email: "",
    password: "",
    confirm_password: "",
    city: "",
    location: "",
    latitude: "",
    longitude: "",
    description: "",
    inventory_type: "both" as "new" | "used" | "both",
  });

  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [otpExpiresAt, setOtpExpiresAt] = useState<string | null>(null);
  const [debugOtp, setDebugOtp] = useState<string | null>(null);

  const [tradeLicense, setTradeLicense] = useState<File | null>(null);
  const [logo, setLogo] = useState<File | null>(null);
  const [submitStatus, setSubmitStatus] = useState<RegisterStatus | null>(null);
  const [loginEmail, setLoginEmail] = useState<string>("");

  const fullMobile = useMemo(
    () => `${selectedCountry.phone_prefix}${form.mobile_local.replace(/\s+/g, "")}`,
    [selectedCountry.phone_prefix, form.mobile_local],
  );

  const logoPreview = useMemo(
    () => (logo ? URL.createObjectURL(logo) : null),
    [logo],
  );
  const tradePreview = useMemo(
    () => (tradeLicense ? URL.createObjectURL(tradeLicense) : null),
    [tradeLicense],
  );

  const set = (key: keyof typeof form, value: string) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const handleSocialAuth = (provider: SocialProvider) => {
    setError("");
    setSocialLoading(provider);
    try {
      authApi.startSocialOAuth({
        provider,
        role: "showroom",
        full_name: form.company_name || undefined,
        email: form.email || undefined,
        phone: fullMobile || undefined,
        country_code: selectedCountry.code,
        city: form.city || undefined,
        vendor_type: "showroom",
        store_name: form.company_name || undefined,
        store_name_ar: form.company_name || undefined,
        next: "/showroom",
      });
    } catch (err: any) {
      const msg =
        err?.message ||
        "تعذر المتابعة عبر التسجيل الاجتماعي حالياً.";
      setError(msg);
      setSocialLoading(null);
    }
  };

  const handleRequestOtp = async () => {
    if (!form.mobile_local.trim()) {
      setError("يرجى إدخال رقم الجوال أولاً.");
      return;
    }
    setOtpLoading(true);
    setError("");
    try {
      const data = await showroomApi.requestOtp(fullMobile);
      setOtpSent(true);
      setOtpVerified(false);
      setOtpExpiresAt(data?.expires_at || null);
      if (data?.otp_code) {
        setDebugOtp(String(data.otp_code));
        set("otp_code", String(data.otp_code));
      } else {
        setDebugOtp(null);
      }
    } catch (err: any) {
      setError(err?.message || "تعذر إرسال OTP حالياً.");
    } finally {
      setOtpLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!form.otp_code.trim()) {
      setError("يرجى إدخال رمز OTP.");
      return;
    }
    setOtpLoading(true);
    setError("");
    try {
      await showroomApi.verifyOtp(fullMobile, form.otp_code.trim());
      setOtpVerified(true);
    } catch (err: any) {
      setOtpVerified(false);
      setError(err?.message || "رمز OTP غير صحيح أو منتهي.");
    } finally {
      setOtpLoading(false);
    }
  };

  const validateStep2 = () => {
    if (!form.company_name.trim()) return "اسم الشركة مطلوب.";
    if (!otpVerified) return "يرجى توثيق رقم الجوال عبر OTP أولاً.";
    if (!form.password || form.password.length < 8) return "كلمة المرور يجب أن تكون 8 أحرف على الأقل.";
    if (form.password !== form.confirm_password) return "كلمتا المرور غير متطابقتين.";
    if (!selectedCountry.code) return "يرجى اختيار الدولة.";
    if (!tradeLicense) return "رفع الرخصة التجارية إلزامي.";
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const validationError = validateStep2();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    setError("");
    try {
      const body = new FormData();
      body.append("company_name", form.company_name.trim());
      body.append("mobile_number", fullMobile);
      body.append("otp_code", form.otp_code.trim());
      body.append("password", form.password);
      body.append("confirm_password", form.confirm_password);
      body.append("country_code", selectedCountry.code);
      body.append("inventory_type", form.inventory_type);
      body.append("submit_for_approval", "true");

      if (form.email.trim()) body.append("email", form.email.trim());
      if (form.city.trim()) body.append("city", form.city.trim());
      if (form.location.trim()) body.append("location", form.location.trim());
      if (form.latitude.trim()) body.append("latitude", form.latitude.trim());
      if (form.longitude.trim()) body.append("longitude", form.longitude.trim());
      if (form.description.trim()) body.append("description", form.description.trim());
      if (tradeLicense) body.append("trade_license", tradeLicense);
      if (logo) body.append("logo", logo);

      const data = await showroomApi.register(body);
      setSubmitStatus((data?.status || "pending") as RegisterStatus);
      setLoginEmail(data?.login_email || form.email.trim());
    } catch (err: any) {
      const msg =
        err?.errors?.otp_code?.[0] ||
        err?.errors?.trade_license?.[0] ||
        err?.errors?.email?.[0] ||
        err?.message ||
        "فشل إرسال طلب المعرض.";
      setError(String(msg));
    } finally {
      setLoading(false);
    }
  };

  if (submitStatus) {
    const meta = statusMeta[submitStatus];
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 flex items-center justify-center p-4">
        <div className="w-full max-w-xl bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl p-8 text-center shadow-2xl">
          <CheckCircle2 className="h-16 w-16 text-emerald-400 mx-auto mb-4" />
          <h1 className="text-2xl font-black text-white mb-2">تم إرسال طلب المعرض بنجاح</h1>
          <p className="text-blue-200 text-sm mb-5">
            يمكنك تسجيل الدخول لمتابعة حالة الاعتماد وإدارة معرضك فور الموافقة.
          </p>
          <div className="flex flex-wrap gap-2 justify-center mb-6">
            <span className={`px-3 py-1.5 rounded-full text-xs font-bold ${meta.color}`}>الحالة: {meta.label}</span>
            {loginEmail && (
              <span className="px-3 py-1.5 rounded-full text-xs font-bold bg-white/10 text-white">
                بريد الدخول: {loginEmail}
              </span>
            )}
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              className="flex-1 h-12 rounded-2xl bg-blue-600 hover:bg-blue-700"
              onClick={() => router.push("/login")}
            >
              تسجيل الدخول
            </Button>
            <Button
              variant="outline"
              className="flex-1 h-12 rounded-2xl border-white/20 text-white hover:bg-white/10"
              onClick={() => router.push("/")}
            >
              العودة للرئيسية
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 flex items-start md:items-center justify-center p-4 pt-8 pb-24 md:pb-8 overflow-y-auto">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/3 -left-24 w-96 h-96 bg-blue-600/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/3 -right-24 w-96 h-96 bg-cyan-600/10 rounded-full blur-3xl" />
      </div>

      <div className="relative w-full max-w-2xl">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-blue-600 rounded-3xl shadow-2xl shadow-blue-900/50 mb-4">
            <CarFront className="h-10 w-10 text-white" />
          </div>
          <h1 className="text-3xl font-black text-white">تسجيل معرض سيارات</h1>
          <p className="text-blue-300 text-sm mt-1">
            تسجيل معرض احترافي مع OTP ولوحة إدارة متقدمة
          </p>
        </div>

        <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl p-8 shadow-2xl">
          <div className="flex gap-2 mb-6">
            {[1, 2].map((s) => (
              <div
                key={s}
                className={`flex-1 h-1.5 rounded-full transition-all ${step >= s ? "bg-blue-500" : "bg-white/10"}`}
              />
            ))}
          </div>

          <form className="space-y-5" onSubmit={handleSubmit}>
            {step === 1 && (
              <>
                <h3 className="text-white font-bold flex items-center gap-2">
                  <Smartphone className="h-4 w-4" />
                  التحقق من رقم الجوال
                </h3>

                <div className="space-y-2">
                  <div className="relative">
                    <div className="h-px bg-white/10" />
                    <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-[#0f172a] px-3 text-[11px] text-white/50 font-bold">
                      تسجيل سريع عبر
                    </span>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <SocialProviderButton
                      provider="facebook"
                      loadingProvider={socialLoading}
                      disabled={loading || otpLoading}
                      onClick={handleSocialAuth}
                    />
                    <SocialProviderButton
                      provider="icloud"
                      loadingProvider={socialLoading}
                      disabled={loading || otpLoading}
                      onClick={handleSocialAuth}
                    />
                    <SocialProviderButton
                      provider="google"
                      loadingProvider={socialLoading}
                      disabled={loading || otpLoading}
                      onClick={handleSocialAuth}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    label="اسم الشركة"
                    value={form.company_name}
                    onChange={(v) => set("company_name", v)}
                    placeholder="مثال: النخبة للسيارات"
                    required
                  />
                  <div>
                    <label className="block text-sm font-bold text-blue-100 mb-1.5">
                      الدولة *
                    </label>
                    <button
                      type="button"
                      onClick={() => setCountryModalOpen(true)}
                      className="w-full bg-white/10 border border-white/20 text-white rounded-2xl px-4 py-3 text-sm flex items-center justify-between hover:bg-white/15 transition"
                    >
                      <span className="flex items-center gap-2">
                        <span className="text-xl">{selectedCountry.emoji}</span>
                        <span>{selectedCountry.name_ar}</span>
                      </span>
                      <ChevronDown className="h-4 w-4 text-white/50" />
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-bold text-blue-100 mb-1.5">
                    رقم الجوال *
                  </label>
                  <div className="flex gap-2">
                    <div className="w-28 bg-white/10 border border-white/20 text-white rounded-2xl px-3 py-3 text-sm flex items-center justify-center">
                      {selectedCountry.phone_prefix}
                    </div>
                    <input
                      value={form.mobile_local}
                      onChange={(e) => set("mobile_local", e.target.value)}
                      required
                      dir="ltr"
                      className="flex-1 bg-white/10 border border-white/20 text-white placeholder-white/40 rounded-2xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
                      placeholder="5XX XXX XXX"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
                  <InputField
                    label="رمز OTP"
                    value={form.otp_code}
                    onChange={(v) => set("otp_code", v)}
                    placeholder="123456"
                    dir="ltr"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    className="h-12 rounded-2xl border-white/20 text-white hover:bg-white/10"
                    disabled={otpLoading}
                    onClick={handleRequestOtp}
                  >
                    {otpLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "إرسال OTP"}
                  </Button>
                  <Button
                    type="button"
                    className="h-12 rounded-2xl bg-blue-600 hover:bg-blue-700"
                    disabled={otpLoading || !otpSent}
                    onClick={handleVerifyOtp}
                  >
                    {otpLoading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : otpVerified ? (
                      <span className="flex items-center gap-2">
                        <ShieldCheck className="h-4 w-4" />
                        تم التحقق
                      </span>
                    ) : (
                      "تحقق"
                    )}
                  </Button>
                </div>

                {(otpSent || otpVerified) && (
                  <div className="bg-blue-500/10 border border-blue-400/30 rounded-2xl p-3 text-xs text-blue-100 space-y-1">
                    {otpExpiresAt && (
                      <p className="flex items-center gap-2">
                        <Timer className="h-3.5 w-3.5" />
                        OTP صالح حتى: {new Date(otpExpiresAt).toLocaleString("ar-SA")}
                      </p>
                    )}
                    {debugOtp && (
                      <p>رمز الاختبار (DEBUG): {debugOtp}</p>
                    )}
                    {otpVerified && <p className="text-emerald-300">تم توثيق رقم الجوال بنجاح.</p>}
                  </div>
                )}

                <Button
                  type="button"
                  className="w-full h-12 rounded-2xl bg-blue-600 hover:bg-blue-700"
                  onClick={() => {
                    if (!otpVerified) {
                      setError("يرجى توثيق رقم الجوال قبل المتابعة.");
                      return;
                    }
                    setError("");
                    setStep(2);
                  }}
                >
                  متابعة بيانات المعرض
                </Button>
              </>
            )}

            {step === 2 && (
              <>
                <h3 className="text-white font-bold flex items-center gap-2">
                  <Building2 className="h-4 w-4" />
                  بيانات المعرض والاعتماد
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    label="البريد الإلكتروني"
                    value={form.email}
                    onChange={(v) => set("email", v)}
                    type="email"
                    dir="ltr"
                    placeholder="example@company.com"
                  />
                  <InputField
                    label="المدينة"
                    value={form.city}
                    onChange={(v) => set("city", v)}
                    placeholder="الرياض"
                  />
                  <InputField
                    label="كلمة المرور"
                    value={form.password}
                    onChange={(v) => set("password", v)}
                    type="password"
                    dir="ltr"
                    required
                  />
                  <InputField
                    label="تأكيد كلمة المرور"
                    value={form.confirm_password}
                    onChange={(v) => set("confirm_password", v)}
                    type="password"
                    dir="ltr"
                    required
                  />
                  <InputField
                    label="الموقع / العنوان"
                    value={form.location}
                    onChange={(v) => set("location", v)}
                    placeholder="حي العليا، شارع الأمير..."
                  />
                  <div className="grid grid-cols-2 gap-2">
                    <InputField
                      label="Latitude"
                      value={form.latitude}
                      onChange={(v) => set("latitude", v)}
                      dir="ltr"
                    />
                    <InputField
                      label="Longitude"
                      value={form.longitude}
                      onChange={(v) => set("longitude", v)}
                      dir="ltr"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-bold text-blue-100 mb-2">
                    نوع مخزون المعرض *
                  </label>
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { key: "new", label: "سيارات جديدة" },
                      { key: "used", label: "سيارات مستعملة" },
                      { key: "both", label: "كلاهما" },
                    ].map((opt) => (
                      <button
                        key={opt.key}
                        type="button"
                        onClick={() => set("inventory_type", opt.key)}
                        className={`rounded-2xl border px-3 py-2 text-sm font-bold transition ${
                          form.inventory_type === opt.key
                            ? "bg-blue-600 text-white border-blue-500"
                            : "bg-white/5 text-blue-100 border-white/20 hover:bg-white/10"
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-bold text-blue-100 mb-1.5">وصف المعرض</label>
                  <textarea
                    value={form.description}
                    onChange={(e) => set("description", e.target.value)}
                    className="w-full min-h-[110px] bg-white/10 border border-white/20 text-white placeholder-white/40 rounded-2xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
                    placeholder="نبذة عن معرضك، العلامات المتوفرة، سياسات البيع..."
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-white/5 border border-white/20 rounded-2xl p-4">
                    <label className="text-sm font-bold text-blue-100 flex items-center gap-2 mb-2">
                      <FileUp className="h-4 w-4" />
                      الرخصة التجارية * (PDF/صورة)
                    </label>
                    <input
                      type="file"
                      accept=".pdf,image/*"
                      onChange={(e) => setTradeLicense(e.target.files?.[0] || null)}
                      className="w-full text-xs text-white/80"
                    />
                    {tradeLicense && (
                      <p className="text-xs text-emerald-300 mt-2">{tradeLicense.name}</p>
                    )}
                    {tradePreview && tradeLicense?.type?.startsWith("image/") && (
                      <img src={tradePreview} alt="trade-license" className="mt-3 w-full h-32 object-cover rounded-xl" />
                    )}
                  </div>

                  <div className="bg-white/5 border border-white/20 rounded-2xl p-4">
                    <label className="text-sm font-bold text-blue-100 flex items-center gap-2 mb-2">
                      <ImageIcon className="h-4 w-4" />
                      شعار المعرض (اختياري)
                    </label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => setLogo(e.target.files?.[0] || null)}
                      className="w-full text-xs text-white/80"
                    />
                    {logo && <p className="text-xs text-emerald-300 mt-2">{logo.name}</p>}
                    {logoPreview && (
                      <img src={logoPreview} alt="logo-preview" className="mt-3 w-full h-32 object-cover rounded-xl bg-white/10" />
                    )}
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    className="flex-1 h-12 rounded-2xl border-white/20 text-white hover:bg-white/10"
                    onClick={() => setStep(1)}
                  >
                    رجوع
                  </Button>
                  <Button
                    type="submit"
                    className="flex-1 h-12 rounded-2xl bg-blue-600 hover:bg-blue-700"
                    disabled={loading}
                  >
                    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "إرسال طلب الاعتماد"}
                  </Button>
                </div>
              </>
            )}

            {error && (
              <div className="bg-red-500/20 border border-red-500/30 rounded-2xl p-3 text-red-300 text-sm text-center">
                {error}
              </div>
            )}

            <div className="pt-3 border-t border-white/10 text-center text-xs text-white/50 space-x-2">
              <Link href="/login" className="text-blue-300 hover:underline">
                تسجيل الدخول
              </Link>
              <span>·</span>
              <Link href="/register" className="text-orange-300 hover:underline">
                تسجيل تاجر عادي
              </Link>
            </div>
          </form>
        </div>
      </div>

      <CountrySelectModal
        isOpen={countryModalOpen}
        onClose={() => setCountryModalOpen(false)}
        onSelect={(c) => setSelectedCountry(c)}
      />
    </div>
  );
}
